import React, { useEffect, useRef, useState } from 'react';
import { Button, Space, Card, Tag, Select, message } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ShoppingOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useNavigate } from 'react-router-dom';
import { Service } from '@/apis';
import {
  DataStats,
  DataTable,
  type ActionButton,
} from '@/components/CommonTable';
import { useDataTableWithSearch } from '@/hooks/useDataTableWithSearch';
import { SearchBar } from '@/components/SearchBar';
import {
  createTimeColumn,
  updateTimeColumn,
  createLongTextColumn,
} from '@/utils/commonUtils';
import { normalizeRows, type ProductRow } from './productShared';
import styles from '@/components/CommonTable/index.module.less';

export type { ProductSpec, ProductRow } from './productShared';

const STATUS_OPTIONS = [
  { value: 'published', label: '已上架' },
  { value: 'draft', label: '草稿' },
  { value: 'offline', label: '下架' },
];

const STATUS_TAG: Record<string, { color: string; text: string }> = {
  published: { color: 'green', text: '已上架' },
  draft: { color: 'default', text: '草稿' },
  offline: { color: 'orange', text: '下架' },
};

const ProductManagement: React.FC = () => {
  const navigate = useNavigate();
  const categoryFilterRef = useRef<string | undefined>(undefined);
  const statusFilterRef = useRef<string | undefined>(undefined);
  const [categoryFilter, setCategoryFilter] = useState<string | undefined>();
  const [statusFilter, setStatusFilter] = useState<string | undefined>();
  const [categories, setCategories] = useState<{ key: string; title: string }[]>([]);

  useEffect(() => {
    Service.ycCategoryControllerGet(1, 500)
      .then((res: unknown) => {
        const rows = (res as { data?: { rows?: unknown[] } })?.data?.rows ?? [];
        setCategories(
          (rows as Record<string, unknown>[]).map((r) => {
            const d = (r.data as Record<string, unknown>) || r;
            const key = String(d.key ?? r.key ?? '');
            return {
              key,
              title: String(d.title ?? r.title ?? key),
            };
          }).filter((c) => c.key)
        );
      })
      .catch(() => setCategories([]));
  }, []);

  const fetchApi = async (pn: number, rn: number, custom?: string) => {
    const res = await Service.ycProductControllerGet(
      pn,
      rn,
      categoryFilterRef.current,
      custom,
      statusFilterRef.current
    );
    const raw = (res as { data?: { rows?: unknown[]; count?: number; pn?: number } })?.data;
    const rows = raw?.rows ?? [];
    return {
      errNo: (res as { errNo?: number }).errNo ?? 0,
      data: {
        rows: normalizeRows(rows),
        count: raw?.count ?? 0,
        pn: raw?.pn ?? pn,
      },
    };
  };

  const {
    data: list,
    loading,
    pagination,
    fetchData,
    handleDelete,
    handlePaginationChange,
    search,
  } = useDataTableWithSearch<ProductRow>({
    fetchData: fetchApi,
    deleteData: async (id) => { await Service.ycProductControllerDelete(id); },
    errorMessage: '获取产品列表失败',
    searchOptions: { fields: ['name', 'category', 'summary', 'price'] },
  });

  const applyFilters = (nextCategory?: string, nextStatus?: string) => {
    categoryFilterRef.current = nextCategory;
    statusFilterRef.current = nextStatus;
    setCategoryFilter(nextCategory);
    setStatusFilter(nextStatus);
    const searchCondition = search.isSearching
      ? search.buildSearchCondition(search.searchValue)
      : '';
    fetchData(1, pagination.pageSize, searchCondition || undefined);
  };

  const setStatus = async (record: ProductRow, status: string) => {
    try {
      await Service.ycProductControllerPut({ id: record.id, status });
      message.success(status === 'published' ? '已上架' : '已下架');
      const searchCondition = search.isSearching
        ? search.buildSearchCondition(search.searchValue)
        : '';
      fetchData(pagination.current, pagination.pageSize, searchCondition || undefined);
    } catch {
      message.error('更新状态失败');
    }
  };

  const categoryTitleMap = Object.fromEntries(categories.map((c) => [c.key, c.title]));

  const columns: ColumnsType<ProductRow> = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
    createLongTextColumn('名称', 'name', 12),
    {
      title: '分类',
      dataIndex: 'category',
      key: 'category',
      width: 140,
      ellipsis: true,
      render: (value: string) => categoryTitleMap[value] || value || '-',
    },
    createLongTextColumn('摘要', 'summary', 15),
    createLongTextColumn('价格', 'price', 8),
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 90,
      render: (value: string) => {
        const item = STATUS_TAG[value || 'published'] || STATUS_TAG.published;
        return <Tag color={item.color}>{item.text}</Tag>;
      },
    },
    {
      title: '精选',
      dataIndex: 'isFeatured',
      key: 'isFeatured',
      width: 80,
      render: (value: number) =>
        value === 1 ? <Tag color="blue">是</Tag> : <span>否</span>,
    },
    createLongTextColumn('主图', 'image', 12),
    createTimeColumn('full'),
    updateTimeColumn('full'),
  ];

  const actionButtons: ActionButton<ProductRow>[] = [
    {
      key: 'edit',
      label: '编辑',
      icon: <EditOutlined />,
      onClick: (record) => navigate(`/cms/product/edit/${record.id}`),
    },
    {
      key: 'toggle-status',
      label: '下架/上架',
      onClick: (record) =>
        setStatus(record, record.status === 'published' ? 'offline' : 'published'),
      confirm: {
        title: '确定更改该产品状态？',
        okText: '确定',
        cancelText: '取消',
      },
    },
    {
      key: 'delete',
      label: '删除',
      icon: <DeleteOutlined />,
      danger: true,
      onClick: (record) => handleDelete(record.id),
      confirm: { title: '确定删除该产品？', okText: '确定', cancelText: '取消' },
    },
  ];

  return (
    <div className={styles.dataTablePage}>
      <Card className={styles.card}>
        <DataStats
          stats={[
            { title: '产品总数', value: pagination.total, prefix: <ShoppingOutlined />, valueStyle: { color: '#3f8600' } },
            { title: '当前页', value: list.length, valueStyle: { color: '#1890ff' } },
          ]}
          className={styles.statsRow}
        />
        <div className={styles.toolbar}>
          <Space wrap>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/cms/product/create')}>
              新增产品
            </Button>
            <Select
              allowClear
              placeholder="按分类筛选"
              style={{ width: 200 }}
              value={categoryFilter}
              options={categories.map((c) => ({ value: c.key, label: c.title }))}
              onChange={(v) => applyFilters(v, statusFilter)}
            />
            <Select
              allowClear
              placeholder="按状态筛选"
              style={{ width: 140 }}
              value={statusFilter}
              options={STATUS_OPTIONS}
              onChange={(v) => applyFilters(categoryFilter, v)}
            />
            <SearchBar search={search} placeholder="搜索名称、分类、摘要、价格" width={300} loading={loading} />
          </Space>
        </div>
        <DataTable
          columns={columns}
          dataSource={list}
          loading={loading}
          rowKey="id"
          pagination={pagination}
          onPaginationChange={handlePaginationChange}
          actionButtons={actionButtons}
        />
      </Card>
    </div>
  );
};

export default ProductManagement;
